import { useEffect, useState } from "react";
import {
  Button,
  Col,
  Row,
  Select,
  Card,
  Tag,
  Tooltip,
  Descriptions,
  Space,
} from "antd";
import "./task-detail.css";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import EduLearnerService from "../services/EduLearnerService";
import { ArrowLeftOutlined } from "@ant-design/icons";
import { CopyClipboard } from "../components/General/CopyClipboard";
import { StartCall } from "../components/General/StartCall";
import { convertDate, openNotification } from "../utils";
import Notes from "../components/EduLearnerDetail/Notes";
import dayjs from "dayjs";

const EduLearnerDetail = () => {
  const [triggerUpdate, setTriggerUpdate] = useState(0);
  const { id } = useParams<{ id: string }>();
  const [learner, setLearner] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [crmStatusOptions, setCrmStatusOptions] = useState<any[]>([]);
  const navigate = useNavigate();
  const location = useLocation();

  const currentPage = location.state?.currentPage ?? 0;
  const pageSize = location.state?.pageSize ?? 20;
  const filterText = location.state?.filterText ?? "";
  const sortByField = location.state?.sortByField ?? "eduCreatedAt";
  const sortByOrder = location.state?.sortByOrder ?? "desc";
  const searchText = location.state?.searchText ?? "";

  useEffect(() => {
    if (id) {
      getLearner();
      loadCrmStatuses();
    }
    // eslint-disable-next-line
  }, [id, triggerUpdate]);

  const getLearner = () => {
    setLoading(true);
    EduLearnerService.get(id!)
      .then((response: any) => {
        setLearner(response.data);
        setLoading(false);
      })
      .catch((e: Error) => {
        console.log(e);
        setLoading(false);
      });
  };

  const loadCrmStatuses = () => {
    EduLearnerService.getCrmStatuses()
      .then((response: any) => {
        const options = response.data.map((item: any) => ({
          value: item.id,
          label: item.name,
        }));
        setCrmStatusOptions(options);
      })
      .catch((e: Error) => {
        console.log("Error loading CRM statuses:", e);
      });
  };

  const handleChangeCrmStatus = (statusId: any) => {
    setLoading(true);
    EduLearnerService.updateCrmStatus(id!, { crmStatusId: statusId })
      .then((response: any) => {
        openNotification("success", "Success", "CRM Status updated");
        getLearner();
      })
      .catch((e: Error) => {
        console.log(e);
        openNotification("error", "Error", "Failed to update CRM Status");
        setLoading(false);
      });
  };

  const handleBack = () => {
    navigate("/edu-learners", {
      state: {
        currentPage: currentPage,
        pageSize: pageSize,
        filterText: filterText,
        sortByField: sortByField,
        sortByOrder: sortByOrder,
        searchText: searchText,
      },
    });
  };

  return (
    <div>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card>
            <Row justify="space-between" align="middle">
              <Col>
                <Button
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBack}
                  style={{ marginRight: 16 }}
                >
                  Back
                </Button>
                <span style={{ fontSize: 20, fontWeight: 600 }}>
                  {learner?.fullname}
                </span>
              </Col>
              <Col>
                <Tag
                  color={learner?.eduStatus === "active" ? "green" : "red"}
                  style={{ fontSize: 14, padding: "4px 12px" }}
                >
                  {learner?.eduStatus?.toUpperCase()}
                </Tag>
              </Col>
            </Row>
          </Card>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: 14 }}>Learner Information</span>} 
            loading={loading}
            bodyStyle={{ padding: '12px' }}
            headStyle={{ padding: '8px 12px', minHeight: 'auto' }}
          >
            <Descriptions column={1} bordered size="small" labelStyle={{ padding: '4px 8px', fontSize: 12 }} contentStyle={{ padding: '4px 8px', fontSize: 12 }}>
              <Descriptions.Item label="Full Name">
                {learner?.fullname}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                <CopyClipboard text={learner?.email} />
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                <Space size={4}>
                  <span>{learner?.phone}</span>
                  <StartCall text={learner?.phone} />
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag
                  color={learner?.eduStatus === "active" ? "green" : "red"}
                  style={{ fontSize: 11, padding: '0 4px', margin: 0 }}
                >
                  {learner?.eduStatus}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="CRM Status">
                <Select
                  value={learner?.crmStatusId}
                  onChange={handleChangeCrmStatus}
                  options={crmStatusOptions}
                  style={{ width: "100%" }}
                  size="small"
                  placeholder="Select CRM Status"
                  loading={loading}
                  showSearch
                  filterOption={(input, option: any) =>
                    option.label.toLowerCase().includes(input.toLowerCase())
                  }
                />
              </Descriptions.Item>
              <Descriptions.Item label="Last Login">
                {learner?.eduLastLogin ? (
                  <div>
                    <div style={{ fontSize: 11 }}>{convertDate(learner.eduLastLogin)}</div>
                    <div style={{ fontSize: 10, color: "#888" }}>
                      {dayjs().diff(dayjs(learner.eduLastLogin), "day")} days ago
                    </div>
                  </div>
                ) : (
                  <Tag color="red" style={{ fontSize: 11, padding: '0 4px', margin: 0 }}>Never logged in</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Created At">
                <span style={{ fontSize: 11 }}>{convertDate(learner?.eduCreatedAt)}</span>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: 14 }}>Groups</span>} 
            loading={loading} 
            style={{ marginBottom: 12 }}
            bodyStyle={{ padding: '12px' }}
            headStyle={{ padding: '8px 12px', minHeight: 'auto' }}
          >
            {learner?.groups && learner.groups.length > 0 ? (
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                {learner.groups.map((group: any, index: number) => {
                  const endDate = dayjs(group.groupEndDate);
                  const today = dayjs();
                  const daysRemaining = endDate.diff(today, "day");
                  const color =
                    daysRemaining < 0
                      ? "red"
                      : daysRemaining < 30
                      ? "orange"
                      : "green";

                  return (
                    <div key={index} style={{ 
                      padding: '6px 8px', 
                      border: '1px solid #f0f0f0', 
                      borderRadius: 4,
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <span style={{ fontSize: 12, fontWeight: 500 }}>{group.groupName}</span>
                      <Tooltip
                        title={`End: ${convertDate(group.groupEndDate)} (${daysRemaining} days ${daysRemaining < 0 ? "expired" : "remaining"})`}
                      >
                        <Tag color={color} style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>
                          {daysRemaining < 0 ? "Expired" : `${daysRemaining}d`}
                        </Tag>
                      </Tooltip>
                    </div>
                  );
                })}
              </Space>
            ) : (
              <div style={{ textAlign: "center", color: "#888", fontSize: 12, padding: '8px 0' }}>
                No groups assigned
              </div>
            )}
          </Card>

          <Card 
            title={<span style={{ fontSize: 14 }}>Courses</span>} 
            loading={loading}
            bodyStyle={{ padding: '12px' }}
            headStyle={{ padding: '8px 12px', minHeight: 'auto' }}
          >
            {learner?.courses && learner.courses.length > 0 ? (
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                {learner.courses.map((course: any, index: number) => (
                  <div key={index} style={{ 
                    padding: '6px 8px', 
                    border: '1px solid #f0f0f0', 
                    borderRadius: 4,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 500 }}>{course.courseName}</div>
                      <div style={{ fontSize: 10, color: "#888" }}>
                        {course.lessonCompletedCount} / {course.lessonTotalCount} lessons
                      </div>
                    </div>
                    <Tag
                      color={
                        course.completionPercentage >= 80
                          ? "green"
                          : course.completionPercentage >= 50
                          ? "orange"
                          : "red"
                      }
                      style={{ fontSize: 11, padding: '2px 6px', margin: 0 }}
                    >
                      {course.completionPercentage}%
                    </Tag>
                  </div>
                ))}
              </Space>
            ) : (
              <div style={{ textAlign: "center", color: "#888", fontSize: 12, padding: '8px 0' }}>
                No courses assigned
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: 14 }}>Hi5 App Packages</span>} 
            loading={loading}
            bodyStyle={{ padding: '12px' }}
            headStyle={{ padding: '8px 12px', minHeight: 'auto' }}
          >
            {learner?.hi5Packages && learner.hi5Packages.length > 0 ? (
              <Space direction="vertical" style={{ width: "100%" }} size={4}>
                {learner.hi5Packages.map((pkg: any, index: number) => {
                  const endDate = dayjs(pkg.endDate);
                  const today = dayjs();
                  const daysRemaining = endDate.diff(today, "day");
                  const isExpired = daysRemaining < 0;
                  const isExpiringSoon = daysRemaining >= 0 && daysRemaining < 7;

                  return (
                    <div key={index} style={{ 
                      padding: '8px', 
                      border: '1px solid #f0f0f0', 
                      borderRadius: 4
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>
                        {pkg.packageName}
                      </div>
                      <Row gutter={[4, 4]}>
                        <Col span={12}>
                          <div style={{ fontSize: 10, color: "#666" }}>
                            <strong>Başlangıç:</strong> {convertDate(pkg.startDate)}
                          </div>
                        </Col>
                        <Col span={12}>
                          <div style={{ fontSize: 10, color: "#666" }}>
                            <strong>Bitiş:</strong> {convertDate(pkg.endDate)}
                            <Tag
                              color={isExpired ? "red" : isExpiringSoon ? "orange" : "green"}
                              style={{ marginLeft: 4, fontSize: 9, padding: '0 4px' }}
                            >
                              {isExpired ? "Expired" : `${daysRemaining}d`}
                            </Tag>
                          </div>
                        </Col>
                        <Col span={12}>
                          <div style={{ fontSize: 10, color: "#666" }}>
                            <strong>Paket:</strong> ${pkg.packagePrice?.toFixed(2) || "0.00"}
                          </div>
                        </Col>
                        <Col span={12}>
                          <div style={{ fontSize: 10, color: "#666" }}>
                            <strong>Alınan:</strong> ${pkg.paidAmount?.toFixed(2) || "0.00"}
                          </div>
                        </Col>
                        <Col span={12}>
                          <div style={{ fontSize: 10, color: "#666" }}>
                            <strong>Yenileme:</strong>{" "}
                            <Tag color={pkg.autoSubsRenewalEnabled ? "green" : "default"} style={{ fontSize: 9, padding: '0 4px', margin: 0 }}>
                              {pkg.autoSubsRenewalEnabled ? "Aktif" : "Pasif"}
                            </Tag>
                          </div>
                        </Col>
                        {pkg.description && (
                          <Col span={24}>
                            <div style={{ fontSize: 10, color: "#666", marginTop: 2 }}>
                              <strong>Açıklama:</strong> {pkg.description}
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>
                  );
                })}
              </Space>
            ) : (
              <div style={{ textAlign: "center", color: "#888", fontSize: 12, padding: '8px 0' }}>
                No packages assigned
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: 14 }}>MongerLab Packages</span>} 
            loading={loading}
            bodyStyle={{ padding: '12px' }}
            headStyle={{ padding: '8px 12px', minHeight: 'auto' }}
          >
            {learner?.mongerPackage ? (
              <div style={{ 
                padding: '8px', 
                border: '1px solid #f0f0f0', 
                borderRadius: 4
              }}>
                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>
                  {learner.mongerPackage.pricingPlanName}
                </div>
                <Row gutter={[4, 4]}>
                  <Col span={12}>
                    <div style={{ fontSize: 10, color: "#666" }}>
                      <strong>Email:</strong> {learner.mongerPackage.email}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ fontSize: 10, color: "#666" }}>
                      <strong>Ürün:</strong> {learner.mongerPackage.productQuantity?.toLocaleString() || "0"}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ fontSize: 10, color: "#666" }}>
                      <strong>Başlangıç:</strong> {convertDate(learner.mongerPackage.subscriptionStartDate)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ fontSize: 10, color: "#666" }}>
                      <strong>Bitiş:</strong> {convertDate(learner.mongerPackage.subscriptionEndDate)}
                      {(() => {
                        const endDate = dayjs(learner.mongerPackage.subscriptionEndDate);
                        const today = dayjs();
                        const daysRemaining = endDate.diff(today, "day");
                        const isExpired = daysRemaining < 0;
                        const isExpiringSoon = daysRemaining >= 0 && daysRemaining < 7;
                        
                        return (
                          <Tag
                            color={isExpired ? "red" : isExpiringSoon ? "orange" : "green"}
                            style={{ marginLeft: 4, fontSize: 9, padding: '0 4px' }}
                          >
                            {isExpired ? "Expired" : `${daysRemaining}d`}
                          </Tag>
                        );
                      })()}
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "#888", fontSize: 12, padding: '8px 0' }}>
                No packages assigned
              </div>
            )}
          </Card>
        </Col>

        <Col xs={24} lg={12}>
          <Card 
            title={<span style={{ fontSize: 14 }}>Oneamz Packages</span>} 
            loading={loading}
            bodyStyle={{ padding: '12px' }}
            headStyle={{ padding: '8px 12px', minHeight: 'auto' }}
          >
            {learner?.oneamzPackage ? (
              <div style={{ 
                padding: '8px', 
                border: '1px solid #f0f0f0', 
                borderRadius: 4
              }}>
                <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 6 }}>
                  {learner.oneamzPackage.pricingPlanName}
                </div>
                <Row gutter={[4, 4]}>
                  <Col span={12}>
                    <div style={{ fontSize: 10, color: "#666" }}>
                      <strong>Email:</strong> {learner.oneamzPackage.email}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ fontSize: 10, color: "#666" }}>
                      <strong>Ürün:</strong> {learner.oneamzPackage.productQuantity?.toLocaleString() || "0"}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ fontSize: 10, color: "#666" }}>
                      <strong>Başlangıç:</strong> {convertDate(learner.oneamzPackage.subscriptionStartDate)}
                    </div>
                  </Col>
                  <Col span={12}>
                    <div style={{ fontSize: 10, color: "#666" }}>
                      <strong>Bitiş:</strong> {convertDate(learner.oneamzPackage.subscriptionEndDate)}
                      {(() => {
                        const endDate = dayjs(learner.oneamzPackage.subscriptionEndDate);
                        const today = dayjs();
                        const daysRemaining = endDate.diff(today, "day");
                        const isExpired = daysRemaining < 0;
                        const isExpiringSoon = daysRemaining >= 0 && daysRemaining < 7;
                        
                        return (
                          <Tag
                            color={isExpired ? "red" : isExpiringSoon ? "orange" : "green"}
                            style={{ marginLeft: 4, fontSize: 9, padding: '0 4px' }}
                          >
                            {isExpired ? "Expired" : `${daysRemaining}d`}
                          </Tag>
                        );
                      })()}
                    </div>
                  </Col>
                </Row>
              </div>
            ) : (
              <div style={{ textAlign: "center", color: "#888", fontSize: 12, padding: '8px 0' }}>
                No packages assigned
              </div>
            )}
          </Card>
        </Col>
      </Row>

      <Row gutter={[12, 12]} style={{ marginTop: 12 }}>
        <Col span={24}>
          <Notes
            learnerId={id}
            setTriggerUpdate={setTriggerUpdate}
          />
        </Col>
      </Row>
    </div>
  );
};

export default EduLearnerDetail;
